import React from "react";
import { Toast, ToastContainer } from "react-bootstrap";
import {
  CheckCircleFill,
  XCircleFill,
  ExclamationTriangleFill,
  InfoCircleFill,
  X,
} from "react-bootstrap-icons";
import "./CustomToast.css";

const CustomToast = ({
  show,
  onClose,
  message,
  variant = "success", // success, error, warning, info
  position = "bottom-end",
  delay = 4000,
  autohide = true,
  isMobile = false,
  // Optional inline action button (e.g., "View" on compile-success toast).
  actionLabel,
  onAction,
  // Optional stack of { id, message, variant } shown together in one container
  // (e.g. Job Setup reports the contract and payment sends separately). When
  // given, it replaces the single-message props above and onClose receives the
  // id of the toast being dismissed.
  messages = null,
}) => {
  const getVariantStyles = (variant) => {
    switch (variant) {
      case "success":
        return {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          color: "white",
          iconColor: "#28a745",
          IconComponent: CheckCircleFill,
        };
      case "error":
        return {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          color: "white",
          iconColor: "#dc3545",
          IconComponent: XCircleFill,
        };
      case "warning":
        return {
          backgroundColor: "rgba(255, 193, 7, 0.9)",
          color: "black",
          iconColor: "#ffc107",
          IconComponent: ExclamationTriangleFill,
        };
      case "info":
        return {
          backgroundColor: "rgba(13, 202, 240, 0.9)",
          color: "white",
          iconColor: "#17a2b8",
          IconComponent: InfoCircleFill,
        };
      default:
        return {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          color: "white",
          iconColor: "#6c757d",
          IconComponent: InfoCircleFill,
        };
    }
  };

  const items = messages || [
    { id: "default", message, variant, show, actionLabel, onAction },
  ];

  return (
    <ToastContainer
      position={isMobile ? "top-center" : position}
      style={{
        position: "fixed",
        zIndex: 9999,
        // Shrink-wrap and clear margin/padding so a parent layout rule can't
        // stretch or re-center the floating toast. Without this, rendering the
        // toast as a direct child of a centered max-width container (e.g.
        // routes.css `.app-main-content.expanded > *`) blows the container up
        // to that width and the toast drifts toward the middle.
        width: "max-content",
        maxWidth: "calc(100vw - 40px)",
        margin: 0,
        padding: 0,
        ...(isMobile
          ? { top: "20px", left: "50%", transform: "translateX(-50%)" }
          : { left: "auto" }),
      }}
      className={`custom-toast-container ${isMobile ? "mobile-toast" : ""}`}
    >
      {items.map((item) => {
        const styles = getVariantStyles(item.variant || variant);
        const IconComponent = styles.IconComponent;
        const handleClose = () => onClose?.(item.id);

        return (
          <Toast
            key={item.id}
            show={item.show !== false}
            onClose={handleClose}
            delay={delay}
            autohide={autohide}
            style={{
              backgroundColor: styles.backgroundColor,
              color: styles.color,
              border: "none",
              minWidth: "300px",
            }}
          >
            <Toast.Body style={{ color: styles.color }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <IconComponent
                  size={16}
                  color={styles.iconColor}
                  style={{ flexShrink: 0 }}
                />
                <span style={{ flex: 1 }}>{item.message}</span>
                {item.actionLabel && item.onAction && (
                  <button
                    type="button"
                    onClick={item.onAction}
                    className="custom-toast-action"
                    aria-label={item.actionLabel}
                  >
                    {item.actionLabel}
                  </button>
                )}
                {!autohide && (
                  <button
                    type="button"
                    onClick={handleClose}
                    style={{
                      background: "none",
                      border: "none",
                      color: styles.color,
                      cursor: "pointer",
                      padding: "0",
                      marginLeft: "8px",
                      display: "flex",
                      alignItems: "center",
                      opacity: 0.7,
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "1")}
                    onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            </Toast.Body>
          </Toast>
        );
      })}
    </ToastContainer>
  );
};

export default CustomToast;
